import { useEffect, useRef, useState } from "react";
import { History, Loader2, Pencil, Plus, RefreshCw, Sparkles, Trash2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { formatDateTime, formatRelative, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, EmptyState, InlineAlert, Skeleton, Switch, cn, notifySaved } from "@ncp/ui";
import { isRunActive, useKnowledgeActions, useKnowledgeRun, type ConsoleKnowledgeCollection, type KnowledgeRun, type KnowledgeRunAction, type KnowledgeRunStatus, type KnowledgeSource } from "../workspaces/api";
import { SOURCE_ICON, SourceDialog, scheduleLabel, sourceLabel } from "./SourceDialog";

type T = ReturnType<typeof useTranslation>["t"];
type Variant = "default" | "outline" | "info" | "success" | "warning" | "danger" | "camel";

const STATUS_VARIANT: Record<KnowledgeRunStatus, Variant> = { Queued: "default", Running: "info", Succeeded: "success", PartiallySucceeded: "warning", Failed: "danger" };
const ACTION_VARIANT: Record<KnowledgeRunAction, Variant> = { added: "success", updated: "info", reembedded: "info", unchanged: "outline", removed: "camel", skipped: "default", failed: "danger" };
const COUNTS = ["added", "updated", "unchanged", "removed", "failed"] as const;

/** Refreshes documents and counts once a queued or running run finishes. */
export function useRunCompletionRefresh(runs: KnowledgeRun[]) {
  const qc = useQueryClient();
  const active = runs.filter(isRunActive).map((r) => r.id).sort().join(",");
  const previous = useRef(active);
  useEffect(() => {
    if (previous.current && previous.current !== active) void qc.invalidateQueries({ queryKey: ["platform", "knowledge"] });
    previous.current = active;
  }, [active, qc]);
}

function RunStatusBadge({ status, t }: { status: KnowledgeRunStatus; t: T }) {
  return <Badge variant={STATUS_VARIANT[status]} size="sm">{status === "Running" || status === "Queued" ? <Loader2 className="size-3 animate-spin" /> : null}{t(`admin:knowledge.runs.status.${status}`)}</Badge>;
}

function RunCounts({ run, t }: { run: KnowledgeRun; t: T }) {
  const parts = COUNTS.filter((c) => run[c] > 0).map((c) => ({ c, label: t(`admin:knowledge.runs.count.${run.sourceKind === "reembed" && c === "updated" ? "reembedded" : c}`, { count: run[c] }) }));
  if (parts.length === 0) return <span className="text-muted-foreground">{isRunActive(run) ? "…" : t("admin:knowledge.runs.noChanges")}</span>;
  return <span className="flex flex-wrap gap-1">{parts.map(({ c, label }) => <Badge key={c} variant={ACTION_VARIANT[c]} size="sm">{label}</Badge>)}</span>;
}

function duration(run: KnowledgeRun, t: T) {
  if (!run.startedAt || !run.completedAt) return null;
  const ms = Math.max(0, new Date(run.completedAt).getTime() - new Date(run.startedAt).getTime());
  return ms < 1000 ? t("admin:knowledge.runs.durationMs", { value: ms }) : ms < 60_000 ? t("admin:knowledge.runs.durationS", { value: (ms / 1000).toFixed(1) }) : t("admin:knowledge.runs.durationMin", { value: Math.round(ms / 60_000) });
}

function runSourceLabel(run: KnowledgeRun, sources: KnowledgeSource[], t: T) {
  if (run.sourceKind === "reembed") return t("admin:knowledge.runs.reembedAll");
  const source = sources.find((s) => s.key === run.sourceKey);
  return source ? sourceLabel(source, t) : run.sourceKey ?? run.sourceKind;
}

/**
 * The collection's synchronised sources. They are edited in the collection's draft like its other settings; a sync reads the saved
 * collection, so it waits until the draft is saved.
 */
export function SourcesCard({ collection, sources, onChange, dirty, canWrite, runs }: {
  collection: ConsoleKnowledgeCollection;
  sources: KnowledgeSource[];
  onChange: (sources: KnowledgeSource[]) => void;
  dirty: boolean;
  canWrite: boolean;
  runs: KnowledgeRun[];
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const actions = useKnowledgeActions();
  const [editing, setEditing] = useState<{ index: number | null } | null>(null);
  const saved = new Map((collection.sources ?? []).filter((s) => s.key).map((s) => [s.key!, s]));
  const rows = sources.map((source, index) => ({ source, index })).filter(({ source }) => source.kind !== "upload");

  const sync = async (source: KnowledgeSource) => {
    try {
      notifySaved(await actions.syncSource.mutateAsync({ key: collection.key, sourceKey: source.key! }), t("admin:knowledge.sources.queued", { name: sourceLabel(source, t) }));
    } catch { /* useSave reported it */ }
  };
  const remove = (index: number) => {
    const source = sources[index]!;
    if (!window.confirm(t("admin:knowledge.sources.removeConfirm", { name: sourceLabel(source, t) }))) return;
    onChange(sources.filter((_, i) => i !== index));
  };
  const apply = (draft: KnowledgeSource) => {
    onChange(editing?.index == null ? [...sources, draft] : sources.map((s, i) => (i === editing.index ? draft : s)));
    setEditing(null);
  };
  const editingSource = editing?.index == null ? null : sources[editing.index] ?? null;

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div><CardTitle className="flex items-center gap-2"><RefreshCw className="size-4 text-aegold-700" /> {t("admin:knowledge.sources.title")}</CardTitle><CardDescription>{t("admin:knowledge.sources.hint")}</CardDescription></div>
        {canWrite ? <Button size="sm" variant="outline" onClick={() => setEditing({ index: null })}><Plus /> {t("admin:knowledge.sources.add")}</Button> : null}
      </CardHeader>
      <CardContent className="space-y-2">
        {dirty && rows.length > 0 ? <InlineAlert tone="warning">{t("admin:knowledge.sources.saveFirst")}</InlineAlert> : null}
        {!collection.enabled ? <InlineAlert tone="info">{t("admin:knowledge.sources.collectionDisabled")}</InlineAlert> : null}
        {rows.length === 0 ? <p className="rounded-xl border border-dashed border-sand-300 p-3 text-xs text-muted-foreground">{t("admin:knowledge.sources.empty")}</p> : rows.map(({ source, index }) => {
          const I = SOURCE_ICON[source.kind];
          const isSaved = !!source.key && saved.has(source.key);
          const last = source.key ? runs.find((r) => r.sourceKey === source.key) : undefined;
          const running = !!last && isRunActive(last);
          const enabled = source.enabled !== false;
          const canSync = canWrite && isSaved && !dirty && enabled && collection.enabled && !running;
          return (
            <div key={source.key ?? index} className={cn("rounded-xl border border-sand-200 p-2.5", !enabled && "opacity-60")}>
              <div className="flex items-start gap-2">
                <I className="mt-0.5 size-4 shrink-0 text-aegold-700" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{sourceLabel(source, t)}</div>
                  <div className="flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground">
                    <span>{t(`admin:knowledge.sources.kind.${source.kind}`)}</span>
                    <span>·</span>
                    <span dir={source.schedule && scheduleLabel(source.schedule, t) === source.schedule ? "ltr" : undefined}>{scheduleLabel(source.schedule, t)}</span>
                    {!isSaved ? <Badge variant="warning" size="sm">{t("admin:common.unsaved")}</Badge> : null}
                  </div>
                </div>
                {canWrite ? <Switch checked={enabled} className="scale-75" aria-label={t("admin:common.enabled")} onCheckedChange={(v) => onChange(sources.map((s, i) => (i === index ? { ...s, enabled: v } : s)))} /> : null}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-1">
                {last ? <><RunStatusBadge status={last.status} t={t} /><span className="text-[11px] text-muted-foreground">{formatRelative(last.completedAt ?? last.createdAt, locale)}</span></> : <span className="text-[11px] text-muted-foreground">{t("admin:knowledge.sources.noRecentRun")}</span>}
                {canWrite ? (
                  <span className="ms-auto flex items-center gap-0.5">
                    <Button size="sm" variant="ghost" disabled={!canSync} loading={running} onClick={() => void sync(source)} title={!isSaved || dirty ? t("admin:knowledge.sources.saveFirst") : undefined}><RefreshCw /> {t("admin:knowledge.sources.syncNow")}</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing({ index })} aria-label={t("admin:common.edit")}><Pencil className="size-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="text-danger-600" onClick={() => remove(index)} aria-label={t("admin:common.remove")}><Trash2 className="size-3.5" /></Button>
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}
      </CardContent>
      {editing ? (
        <SourceDialog
          collectionKey={collection.key}
          source={editingSource}
          saved={!!editingSource?.key && saved.has(editingSource.key)}
          existingKeys={sources.filter((_, i) => i !== editing.index).map((s) => s.key).filter((k): k is string => !!k)}
          defaultKind={collection.defaultDocumentKind}
          onSave={apply}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </Card>
  );
}

/** Recent synchronisation and re-embedding runs of the collection, with what each one filed. */
export function RunsCard({ collection, runs, loading, canWrite }: { collection: ConsoleKnowledgeCollection; runs: KnowledgeRun[]; loading: boolean; canWrite: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const actions = useKnowledgeActions();
  const [openRun, setOpenRun] = useState<string | null>(null);
  const sources = collection.sources ?? [];
  const reembedding = runs.some((r) => r.sourceKind === "reembed" && isRunActive(r));

  const reembed = async () => {
    if (!window.confirm(t("admin:knowledge.runs.reembedConfirm", { documents: collection.documents }))) return;
    try { notifySaved(await actions.reembed.mutateAsync(collection.key), t("admin:knowledge.runs.reembedQueued")); } catch { /* useSave reported it */ }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div><CardTitle className="flex items-center gap-2"><History className="size-4 text-aegold-700" /> {t("admin:knowledge.runs.title")}</CardTitle><CardDescription>{t("admin:knowledge.runs.hint")}</CardDescription></div>
        {canWrite ? <Button size="sm" variant="outline" disabled={collection.documents === 0 || !collection.enabled} loading={reembedding || actions.reembed.isPending} onClick={() => void reembed()}><Sparkles /> {t("admin:knowledge.runs.reembed")}</Button> : null}
      </CardHeader>
      <CardContent>
        {loading ? <Skeleton className="h-24" /> : runs.length === 0 ? <EmptyState icon={History} title={t("admin:knowledge.runs.emptyTitle")} description={t("admin:knowledge.runs.emptyBody")} className="py-6" /> : (
          <div className="overflow-x-auto rounded-xl border border-sand-200">
            <table className="w-full text-sm">
              <thead className="bg-sand-50 text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-start font-medium">{t("admin:knowledge.runs.source")}</th>
                  <th className="px-3 py-2 text-start font-medium">{t("admin:knowledge.runs.statusLabel")}</th>
                  <th className="px-3 py-2 text-start font-medium">{t("admin:knowledge.runs.result")}</th>
                  <th className="px-3 py-2 text-start font-medium">{t("admin:knowledge.runs.when")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-sand-100">
                {runs.map((r) => (
                  <tr key={r.id} className="cursor-pointer hover:bg-sand-50/60" onClick={() => setOpenRun(r.id)}>
                    <td className="px-3 py-2"><div className="font-medium">{runSourceLabel(r, sources, t)}</div><div className="text-[11px] text-muted-foreground">{t(`admin:knowledge.runs.trigger.${r.trigger}`)}{r.requestedByName && r.trigger === "Manual" ? ` · ${r.requestedByName}` : ""}</div></td>
                    <td className="px-3 py-2"><RunStatusBadge status={r.status} t={t} /></td>
                    <td className="px-3 py-2 text-xs"><RunCounts run={r} t={t} />{r.error ? <div className="mt-0.5 line-clamp-1 text-[11px] text-danger-700">{r.error}</div> : null}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground"><div>{formatRelative(r.createdAt, locale)}</div>{duration(r, t) ? <div className="text-[11px]">{duration(r, t)}</div> : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      {openRun ? <RunDialog collectionKey={collection.key} runId={openRun} sources={sources} onClose={() => setOpenRun(null)} /> : null}
    </Card>
  );
}

function RunDialog({ collectionKey, runId, sources, onClose }: { collectionKey: string; runId: string; sources: KnowledgeSource[]; onClose: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const run = useKnowledgeRun(collectionKey, runId);
  const [filter, setFilter] = useState<KnowledgeRunAction | "">("");
  const r = run.data;
  const items = r?.items ?? [];
  const actionsPresent = [...new Set(items.map((i) => i.action))];
  const shown = filter ? items.filter((i) => i.action === filter) : items;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{r ? runSourceLabel(r, sources, t) : t("admin:knowledge.runs.title")}</DialogTitle>
          <DialogDescription>
            {r ? t("admin:knowledge.runs.detailSummary", { trigger: t(`admin:knowledge.runs.trigger.${r.trigger}`), who: r.requestedByName ?? "—", when: formatDateTime(r.createdAt, locale) }) : null}
          </DialogDescription>
        </DialogHeader>
        {!r ? <Skeleton className="h-40" /> : (
          <div className="min-w-0 space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <RunStatusBadge status={r.status} t={t} />
              <RunCounts run={r} t={t} />
              <span className="text-muted-foreground">{t("admin:knowledge.runs.seen", { count: r.itemsSeen })}</span>
              {r.chunks > 0 ? <span className="text-muted-foreground">· {t("admin:knowledge.runs.chunks", { count: r.chunks })}</span> : null}
              {duration(r, t) ? <span className="text-muted-foreground">· {duration(r, t)}</span> : null}
            </div>
            {r.error ? <InlineAlert tone="danger">{r.error}</InlineAlert> : null}
            {actionsPresent.length > 1 ? (
              <div className="flex flex-wrap gap-1">
                <Button size="sm" variant={filter === "" ? "gold" : "outline"} onClick={() => setFilter("")}>{t("admin:common.all")} ({items.length})</Button>
                {actionsPresent.map((a) => <Button key={a} size="sm" variant={filter === a ? "gold" : "outline"} onClick={() => setFilter(a)}>{t(`admin:knowledge.runs.action.${a}`)} ({items.filter((i) => i.action === a).length})</Button>)}
              </div>
            ) : null}
            {shown.length === 0 ? <p className="text-xs text-muted-foreground">{isRunActive(r) ? t("admin:knowledge.runs.inProgress") : t("admin:knowledge.runs.noItems")}</p> : (
              <ul className="max-h-[50vh] divide-y divide-sand-100 overflow-y-auto rounded-xl border border-sand-200 text-xs">
                {shown.map((i, n) => (
                  <li key={`${i.item}-${n}`} className="flex items-start gap-2 px-3 py-2">
                    <Badge variant={ACTION_VARIANT[i.action] ?? "default"} size="sm" className="mt-0.5 shrink-0">{t(`admin:knowledge.runs.action.${i.action}`)}</Badge>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{i.title}</div>
                      <div className="truncate font-mono text-[10px] text-muted-foreground" dir="ltr">{i.item}</div>
                      {i.detail ? <div className={cn("text-[11px]", i.action === "failed" ? "text-danger-700" : "text-muted-foreground")}>{i.detail}</div> : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
